import { useParams } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import { m } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getActiveClient, isPreviewMode } from "../../lib/sanity";
import { useLanguage } from "../../hooks/useLanguage";
import PreviewBanner from "../../Components/PreviewBanner";
import { PortableText } from "@portabletext/react";
import { urlForImage, urlForImageWithVanity } from "../../lib/sanityImageUrl";
import { buildBlogPath } from "../../utils/languageUtils";
import PostLayout from "./PostLayout";
import PortableTextComponents from "./SanityComponents/PortableTextComponents";
import NewsletterPromoPopup from "../../Components/NewsletterPromoPopup";
import { BookSVG } from "../../Components/BookSVG";
import { GoShare } from "react-icons/go";
import { GoThumbsup } from "react-icons/go";
import { FaLink, FaFacebook, FaLinkedin } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { extractHeadings } from "../../utils/tableOfContents";
import {
  buildBlogSiblingEntry,
  getVariantSlug,
  isApprovedVariant,
  DEFAULT_LANGUAGE,
} from '../../utils/languageUtils';
import BlogBreadcrumb from '../../Components/Breadcrumb/BlogBreadcrumb';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const calculateReadTime = (body) => {
  if (!body) return 0;
  const wordsPerMinute = 200;
  let totalWords = 0;
  body.forEach((block) => {
    if (block._type === "block") {
      const text = block.children
        .filter((child) => child._type === "span")
        .map((span) => span.text)
        .join(" ");
      totalWords += text.split(/\s+/).length;
    }
  });
  return Math.ceil(totalWords / wordsPerMinute);
};

// Custom component that renders the newsletter at a specific position
const NewsletterBlock = () => {
  return (
    <div className='newsletter-block my-8'>
      <NewsletterPromoPopup />
    </div>
  );
};

// Debug function to inspect a block and determine if it's a paragraph
const isParagraphBlock = (block) => {
  // Check if it's a text block
  const isBlockType = block._type === "block";
  // Check if it has normal style (not a heading)
  const isNormalStyle = block.style === "normal";
  // Check it's not a list item
  const isNotListItem = !block.listItem;
  // Check if it has content (not empty)
  const hasContent =
    block.children &&
    block.children.some(
      (child) =>
        child._type === "span" && child.text && child.text.trim() !== ""
    );
  return isBlockType && isNormalStyle && isNotListItem && hasContent;
};

const BlogPost = () => {
  const { t } = useTranslation(['common', 'blog']);
  const { slug } = useParams();
  const { currentLang } = useLanguage();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claps, setClaps] = useState(0);
  const [hasClapped, setHasClapped] = useState(false);
  const [isClapping, setIsClapping] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Copy to clipboard function
  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(window.location.href);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }, []);

  // Process the post content to insert newsletter
  const processedContent = useMemo(() => {
    if (!post || !post.body) return null;

    // Find all normal paragraphs with careful filtering
    const paragraphs = post.body.filter((block) => isParagraphBlock(block));
    console.log("Identified paragraphs:", paragraphs.length, paragraphs);

    // Make sure we have at least 3 paragraphs before inserting the newsletter
    if (paragraphs.length >= 3) {
      // Get the 3rd-to-last paragraph
      const thirdFromLastIndex = paragraphs.length - 2;
      const thirdFromLastParagraph = paragraphs[thirdFromLastIndex];
      console.log("Third-from-last paragraph:", thirdFromLastParagraph);

      // Find its position in the original body content
      const thirdFromLastPosition = post.body.findIndex(
        (block) => block._key === thirdFromLastParagraph._key
      );
      console.log("Position in content:", thirdFromLastPosition);

      if (thirdFromLastPosition !== -1) {
        // Create a new array with the newsletter inserted BEFORE the third-from-last paragraph
        const newContent = [
          ...post.body.slice(0, thirdFromLastPosition),
          // Add the newsletter block before the paragraph
          { _type: "newsletterBlock", _key: "newsletter-" + Date.now() },
          ...post.body.slice(thirdFromLastPosition),
        ];
        console.log("Content with newsletter:", newContent);
        return newContent;
      }
    }

    // If we couldn't find appropriate paragraphs or the position, return the original content
    return post.body;
  }, [post]);

  // Extended components with newsletter handler
  const extendedComponents = useMemo(() => {
    return {
      ...PortableTextComponents,
      types: {
        ...PortableTextComponents.types,
        newsletterBlock: () => <NewsletterBlock />,
        tableOfContents: ({ value }) => {
          console.log("Rendering tableOfContents with value:", value);

          try {
            // Extract headings from the post body
            const headings = extractHeadings(post?.body || []);
            console.log("Extracted headings:", headings);

            if (headings.length === 0) {
              return (
                <div className='table-of-contents-container'>
                  <h2 className='table-of-contents-title'>
                    {value?.title || t('blog:tocTitle', 'V článku sa dozviete')}
                  </h2>
                  <p className='table-of-contents-empty'>
                    {t('blog:tocEmpty', 'No headings found in this article.')}
                  </p>
                </div>
              );
            }

            return (
              <div className='table-of-contents-container'>
                <h2 className='table-of-contents-title'>
                  {value?.title || "V článku sa dozviete"}
                </h2>
                <nav className='table-of-contents-nav'>
                  <ul>
                    {headings.map((heading, index) => (
                      <li key={index} className={`toc-level-${heading.level}`}>
                        <a
                          href={`#${heading.slug}`}
                          className='toc-link'
                          onClick={(e) => {
                            e.preventDefault();
                            const element = document.getElementById(
                              heading.slug
                            );
                            if (element) {
                              element.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            } else {
                              console.warn(
                                `Element with ID #${heading.slug} not found`
                              );
                            }
                          }}
                        >
                          {heading.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            );
          } catch (error) {
            console.error("Error rendering Table of Contents:", error);
            return (
              <div className='table-of-contents-error'>
                <h2 className='table-of-contents-error-title'>
                  Table of Contents Error
                </h2>
                <p className='table-of-contents-error-message'>
                  {error.message ||
                    "An error occurred rendering the table of contents"}
                </p>
              </div>
            );
          }
        },
      },
      context: {
        blocks: post?.body || [],
      },
    };
  }, [post?.body]);

  useEffect(() => {
    if (post?.body) {
      console.log(
        "Found post body with types:",
        post.body
          .map((block) => block._type)
          .filter((type, i, arr) => arr.indexOf(type) === i)
      );

      // Find tableOfContents blocks
      const tocBlocks = post.body.filter(
        (block) => block._type === "tableOfContents"
      );
      console.log("TableOfContents blocks:", tocBlocks);
    }
  }, [post?.body]);

  useEffect(() => {
    if (post?.body) {
      // Find h2, h3, h4 blocks
      const headingBlocks = post.body.filter(
        (block) =>
          block._type === "block" && ["h2", "h3", "h4"].includes(block.style)
      );

      console.log("Heading blocks found:", headingBlocks);

      // Test the extractHeadings function
      const extractedHeadings = extractHeadings(post.body);
      console.log("Extracted headings:", extractedHeadings);
    }
  }, [post?.body]);

  useEffect(() => {
    if (post?.body) {
      setTimeout(() => {
        console.log("Checking TOC and heading IDs:");

        // Log all heading elements with their IDs
        const headings = document.querySelectorAll("h2, h3, h4");
        console.log(`Found ${headings.length} heading elements in DOM`);

        headings.forEach((heading) => {
          console.log(
            `Heading: "${heading.textContent.trim()}" has ID: "${heading.id}"`
          );
        });

        // Log all TOC links
        const tocLinks = document.querySelectorAll(".toc-link");
        console.log(`Found ${tocLinks.length} TOC links`);

        tocLinks.forEach((link) => {
          const href = link.getAttribute("href");
          console.log(
            `TOC link: "${link.textContent.trim()}" points to: "${href}"`
          );

          // Check if target exists
          const targetId = href.substring(1); // Remove the # character
          const targetExists = !!document.getElementById(targetId);
          console.log(`  Target element exists: ${targetExists}`);

          if (!targetExists) {
            // Find closest match to help debugging
            const allIds = Array.from(document.querySelectorAll("[id]")).map(
              (el) => el.id
            );
            console.log(`  All available IDs: ${allIds.join(", ")}`);
          }
        });
      }, 500); // Wait for content to be fully rendered
    }
  }, [post?.body]);

  useEffect(() => {
    setClaps(post?.claps || 0);
    const hasUserClapped = localStorage.getItem(`clapped_${post?._id}`);
    setHasClapped(!!hasUserClapped);
  }, [post?._id, post?.claps]);

  const handleClap = async () => {
    if (hasClapped || isClapping || !post._id) return;
    setIsClapping(true);
    try {
      const documentId = `${post._id.replace("drafts.", "")}`;
      const newClaps = claps + 1;
      setClaps(newClaps);
      setHasClapped(true);

      const response = await fetch(`/api/interactions/posts/${encodeURIComponent(documentId)}/claps`, {
        method: "POST",
      });
      if (!response.ok) throw new Error(`Clap API error: ${response.status}`);
      const updatedPost = await response.json();
      setClaps(updatedPost.claps);
      localStorage.setItem(`clapped_${documentId}`, "true");
    } catch (error) {
      console.error("Error updating claps:", error);
      setClaps((prev) => prev - 1);
      setHasClapped(false);
    } finally {
      setIsClapping(false);
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // Query including SEO fields and proper image fields
        const query = `*[_type == "post" && (slug.current == $slug || slug == $slug || $slug in localeVariants[].slug.current || $slug in localeVariants[].slug)][0]{
        _id,
        language,
        title,
        slug,
        excerpt,
        mainImage {
          asset->{
            _id,
            url,
            originalFilename,
            title,
            description,
            altText,
            metadata {
              dimensions {
                width,
                height
              }
            }
          },
          caption,
          alt
        },
        claps,  
        body[]{
          ...,
          _type == "block" => {
            ...,
            markDefs[]{
              ...,
              _type == "link" => {
                "href": href
              },
              _type == "tooltip" => {
                "text": text
              }
            }
          },
          _type == "tableOfContents" => {
            _type,
            title,
            includeHeadings
          },
          _type == "image" => {
            ...,
            asset->{
              _id,
              url,
              originalFilename,
              title,     
              description,  
              altText, 
              metadata {
                dimensions {
                  width,
                  height
                }
              }
            },
            caption,
            alt
          },
          _type == "linkedInEmbed" => {
            _type,
            url,
            kind,
            caption
          },
          _type == "customTweet" => {
            ...,
            author {
              ...,
              avatar {
                asset->{
                  _id,
                  url,
                  originalFilename,
                  metadata {
                    dimensions {
                      width,
                      height
                    }
                  }
                }
              }
            }
          },
          _type == "pollReference" => {
            ...,
            "poll": *[_type == "pollContent" && _id == ^._ref][0]{
              question,
              options,
              caption,
              _id
            }
          },
          _type == "faqSectionBlock" => {
            _type,
            faqData{
              heading,
              faqItems[]{
                question,
                answer
              }
            }
          },
          markDefs[]{
            ...,
            _type == "internalLink" => {
              "slug": @.reference->slug
            },
            _type == "tooltip" => {
              "text": text
            }
          }
        },
        publishedAt,
        categories[]->{
          title
        },
        localeVariants[]{
          language,
          title,
          slug,
          excerpt,
          body[]{...},
          seo,
          status,
          indexable,
          slugLocked,
          manualOverride
        },
        author->{
          name,
          image {
            asset->{
              _id,
              url,
              originalFilename,
              title,
              description,
              altText,
              metadata {
                dimensions {
                  width,
                  height
                }
              }
            },
            caption,
            alt
          }
        },
        seo{
          metaTitle,
          metaDescription,
          seoKeywords,
          nofollowAttributes,
          openGraph{
            title,
            description,
            siteName,
            url,
            image{
              asset->{
                _id,
                url,
                originalFilename,
                metadata {
                  dimensions {
                    width,
                    height
                  }
                }
              },
              caption,
              alt
            }
          },
          twitter{
            handle,
            site,
            creator,
            cardType
          },
          additionalMetaTags[]{
            metaAttributes[]{
              attributeKey,
              attributeType,
              attributeValueString,
              attributeValueImage{
                asset->{
                  _id,
                  url,
                  originalFilename,
                  metadata {
                    dimensions {
                      width,
                      height
                    }
                  }
                },
                caption,
                alt
              }
            }
          },
          localeVariants[]{
            language,
            title,
            slug,
            excerpt,
            body[]{...},
            seo,
            status,
            indexable,
            slugLocked,
            manualOverride
          }
        }
        }`;
        const fetchedPost = await getActiveClient().fetch(query, { slug });
        if (fetchedPost) {
          const sourceSlug = getVariantSlug(fetchedPost);
          const requestedVariant = (fetchedPost.localeVariants || []).find(
            (variant) => getVariantSlug(variant) === slug
          );
          const approvedVariant = requestedVariant &&
            requestedVariant.language === currentLang &&
            isApprovedVariant(requestedVariant)
            ? requestedVariant
            : null;
          const shouldUseSource = sourceSlug === slug &&
            (currentLang === DEFAULT_LANGUAGE || !approvedVariant);
          const selectedPost = approvedVariant
            ? {
                ...fetchedPost,
                ...approvedVariant,
                _id: fetchedPost._id,
                sourcePostId: fetchedPost._id,
                sourceLanguage: fetchedPost.language || DEFAULT_LANGUAGE,
                sourceSlug,
                locale: currentLang,
                localeVariants: fetchedPost.localeVariants,
              }
            : shouldUseSource
              ? { ...fetchedPost, locale: fetchedPost.language || DEFAULT_LANGUAGE }
              : null;

          if (!selectedPost) {
            setError(`No approved ${currentLang} translation for this article`);
            return;
          }

          setPost(selectedPost);
          setClaps(selectedPost.claps || 0);
          const hasUserClapped = localStorage.getItem(
            `clapped_${selectedPost._id}`
          );
          setHasClapped(!!hasUserClapped);
        } else {
          setError("Post not found in Sanity dataset");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(`Error fetching post: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, currentLang]);

  // Prepare SEO data regardless of loading/error state
  const postTitle = post?.title || "INAQ Blog";
  const postExcerpt = post?.excerpt || "";
  const postImage = post?.seo?.openGraph?.image?.asset?.url ||
    post?.mainImage?.asset?.url || "";
  const postSeoData = post?.seo || null;
  const postSlug = post ? buildBlogPath(currentLang, post.slug?.current) : buildBlogPath(currentLang, slug);
  const categoryText =
    post?.categories && post.categories.length > 0
      ? ` | ${post.categories[0].title}`
      : "";
  const defaultTitle = post ? `${postTitle}${categoryText}` : "INAQ Blog";
  const structuredData = post
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        image: postImage
          ? [postImage]
          : ["https://inaq.sk/assets/img/webp/inaq_blog_logo_web.webp"],
        datePublished: post.publishedAt,
        dateModified: post._updatedAt || post.publishedAt,
        author: {
          "@type": "Person",
          name: post.author?.name || "INAQ",
        },
        publisher: {
          "@type": "Organization",
          name: "INAQ",
          logo: {
            "@type": "ImageObject",
            url: "https://www.inaq.sk/assets/img/webp/inaq_logo_web.webp",
          },
        },
        keywords:
          (post.seo?.seoKeywords?.length > 0
            ? post.seo.seoKeywords.join(", ")
            : post.categories?.map((cat) => cat.title).join(", ")) ||
          "B2B, marketing",
        description: post.excerpt,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://www.inaq.sk/blog/${post.slug?.current}`,
        },
      }
    : null;

  const approvedSiblings = useMemo(
    () => (post?.localeVariants || [])
      .map(buildBlogSiblingEntry)
      .filter(Boolean),
    [post?.localeVariants]
  );

  return (
    <>
      {isPreviewMode() && <PreviewBanner />}
      <PostLayout
        seo={postSeoData}
        slug={postSlug}
        defaultTitle={defaultTitle}
        defaultDescription={postExcerpt}
        defaultOgImage={postImage}
        hideNewsletter={true}
        structuredData={structuredData}
        approvedSiblings={approvedSiblings}
        forceLang={currentLang}
      >
        {loading ? (
          <div className='max-w-[1280px] mx-auto px-[15px]'>
            <div>Loading post... Please wait.</div>
          </div>
        ) : error ? (
          <div className='max-w-[1280px] mx-auto px-[15px]'>
            <div>Error: {error}</div>
            <div>Attempted to load slug: {slug}</div>
          </div>
        ) : !post ? (
          <div className='max-w-[1280px] mx-auto px-[15px]'>
            <div>Post not found</div>
            <div>Attempted to load slug: {slug}</div>
          </div>
        ) : (
          <div className='max-w-[680px] mt-12 md:mt-8 pt-[80px] mx-auto'>
            <BlogBreadcrumb
              currentLang={currentLang}
              category={post.categories?.[0]?.title}
              localeVariants={post.localeVariants || []}
              sourceLanguage={post.sourceLanguage || post.language || DEFAULT_LANGUAGE}
              sourceSlug={post.sourceSlug || getVariantSlug(post)}
            />
            <m.div
              className='visible overflow-hidden rounded-[6px] text-center p-0 xxxl:mx-10 xxs:mx-4'
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className='blog-title text-left font-[Oxanium] font-bold text-4xl text-darkgray mb-2 mt-3 sm:mt-2 md:mt-4 relative'>
                {post.title}
              </h1>
              <div className='border-none py-1 mb-8'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center space-x-6'>
                    <div className='flex items-center space-x-0'>
                      {post.author?.image?.asset && (
                        <img
                          src={urlForImage(post.author.image)
                            .width(32)
                            .height(32)
                            .url()}
                          alt={post.author.name}
                          className='w-8 h-8 rounded-full'
                        />
                      )}
                      <span className='text-gray-700 text-sm cursor-default'>
                        {t('blog:author', 'Autor')} {post.author?.name}
                      </span>
                      <span className='mx-2 text-gray-500 cursor-default'>
                        •
                      </span>
                    </div>
                    <div className='flex items-center space-x-2 mx-0 text-gray-500 text-sm'>
                      <span className='mr-2 cursor-default'>
                        {formatDate(post.publishedAt)}
                      </span>
                      <BookSVG className='w-4 h-4 mobs:hidden' />
                      <span className='cursor-default  mobs:hidden'>
                        {calculateReadTime(post.body)} {t('common:blog.readTime', 'min čítania')}
                      </span>
                    </div>
                  </div>
                  <div className='flex items-center space-x-4'>
                    <button
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: post.title,
                            url: window.location.href,
                          });
                        }
                      }}
                      className='text-gray-500 hover:text-gray-700'
                    >
                      <GoShare className='w-5 h-5' />
                    </button>
                    <button
                      onClick={handleClap}
                      className={`flex items-center space-x-1 ${
                        hasClapped
                          ? "text-[#612B64]"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                      disabled={hasClapped || isClapping}
                    >
                      <GoThumbsup className='w-5 h-5' />
                      <span className='text-sm'>{claps}</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className='blog-image'>
                {post.mainImage && (
                  <figure>
                    <img
                      className='w-full'
                      src={urlForImageWithVanity(post.mainImage)}
                      alt={
                        post.mainImage.asset?.altText ||
                        post.mainImage.asset?.title ||
                        post.mainImage.alt ||
                        post.title
                      }
                      title={
                        post.mainImage.asset?.title ||
                        post.mainImage.asset?.description ||
                        post.mainImage.caption ||
                        post.mainImage.asset?.altText ||
                        post.mainImage.alt ||
                        post.title
                      }
                    />
                    {post.mainImage.caption && (
                      <figcaption className='text-center font-rubik text-md text-gray-500 mt-3 main-image-caption'>
                        {post.mainImage.caption}
                      </figcaption>
                    )}
                  </figure>
                )}
              </div>
              <div className='blog-text inline-block w-full'>
                <div className='relative mx-auto mt-12 text-[17px] text-gray-900 font-rubik lg:w-full'>
                  <div className='text-left'>
                    <PortableText
                      value={processedContent || post.body}
                      components={extendedComponents}
                      context={{
                        blocks: post.body,
                      }}
                    />

                    {/* Share Section */}
                    <div className='share-section mt-10 pt-6 border-t border-gray-300'>
                      <div className='flex justify-between items-start flex-wrap'>
                        <div className='mb-4 md:mb-0 xs:hidden'>
                          {post.categories && post.categories.length > 0 && (
                            <div className='flex items-center bg-inaqgold text-white text-xs px-3 py-1 rounded-sm cursor-default'>
                              <span>{post.categories[0].title}</span>
                            </div>
                          )}
                        </div>

                        <div className='flex items-center gap-4 xs:mx-auto'>
                          <p className='text-gray-500 text-sm cursor-default'>
                            {t('blog:shareLabel', 'Zdieľajte tento článok:')}
                          </p>

                          <div className='relative'>
                            <button
                              onClick={copyToClipboard}
                              className='text-gray-500 hover:text-inaqgold transition-colors'
                              aria-label={t('blog:copyLinkAria', 'Copy link')}
                            >
                              <FaLink size={15} />
                            </button>
                            {linkCopied && (
                              <div className='absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-inaqgold text-white text-xs rounded-sm whitespace-nowrap'>
                                {t('blog:linkCopied', 'Odkaz skopírovaný')}
                              </div>
                            )}
                          </div>

                          <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-gray-500 hover:text-inaqgold transition-colors'
                            aria-label={t('blog:shareFacebookAria', 'Zdielať na Facebook')}
                          >
                            <FaFacebook size={16} />
                          </a>

                          <a
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-gray-500 hover:text-inaqgold transition-colors'
                            aria-label={t('blog:shareLinkedinAria', 'Zdielať na LinkedIn')}
                          >
                            <FaLinkedin size={16} />
                          </a>

                          <a
                            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-gray-500 hover:text-inaqgold transition-colors'
                            aria-label={t('blog:shareXAria', 'Zdielať na X')}
                          >
                            <FaXTwitter size={16} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </m.div>
          </div>
        )}
      </PostLayout>
    </>
  );
};

export default BlogPost;
